import useClickOutside from '@/hooks/use-click-outside';
import { toast } from '@/hooks/use-toast';
import { pdkAxios } from '@/lib/pdk-axios';
import type { CustomerType } from '@/models';
import { customerAddressStore } from '@/models/stores';
import type { Address, Deal } from '@/models/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce, isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Command, CommandInput, CommandItem, CommandList } from '../command';
import InlineTextInput from '../inline-text-input';
import {
  fetchAutocompleteAddresses,
  type MapBoxAddressType,
} from './fetchAutocompleteAddresses';

const AddressForm = ({
  focusOnMount = false,
  address,
  dealId,
  customerIdOrCoregistrantId,
  customerType,
}: {
  focusOnMount?: boolean;
  address: Address;
  dealId: Deal['id'];
  customerIdOrCoregistrantId: string;
  customerType: CustomerType;
}) => {
  const address2Ref = useRef<HTMLInputElement>(null);
  const address1Ref = useRef<HTMLInputElement>(null);
  const [hasFocusedOnMount, setHasFocusedOnMount] = useState(false);
  const [options, setOptions] = useState<MapBoxAddressType[]>([]);
  const [address1, setAddress1] = useState(address.address_line_1);
  const [address2, setAddress2] = useState(address.address_line_2);
  const [city, setCity] = useState(address.city);
  const [state, setState] = useState(address.state);
  const [zip, setZip] = useState(address.postal_code);
  const [address1IsFocused, setAddress1IsFocused] = useState(false);

  const queryClient = useQueryClient();

  const updateCustomerRegistrationAddressMutation = useMutation({
    onMutate: async ({ address }) => {
      if (customerType === 'primary') {
        if (address.address_type === 'registration') {
          customerAddressStore.updateByCustomerIdAndAddressType({
            customerId: customerIdOrCoregistrantId,
            addressType: address.address_type,
            address,
          });
          // dealStore.updatePrimaryCustomerRegistrationAddress(
          //   customerIdOrCoregistrantId,
          //   address,
          // );
        } else if (address.address_type === 'delivery') {
          customerAddressStore.updateByCustomerIdAndAddressType({
            customerId: customerIdOrCoregistrantId,
            addressType: address.address_type,
            address,
          });
          // dealStore.updatePrimaryCustomerDeliveryAddress({
          //   id: customerIdOrCoregistrantId,
          //   deliveryAddress: address,
          // });
        }
      }

      if (customerType === 'co_registrant') {
        if (address.address_type === 'registration') {
          customerAddressStore.updateByCustomerIdAndAddressType({
            customerId: customerIdOrCoregistrantId,
            addressType: address.address_type,
            address,
          });
          // dealStore.updateCoRegistrantRegistrationAddress(
          //   customerIdOrCoregistrantId,
          //   address,
          // );
        }
      }
    },
    mutationFn: async ({ address }: { address: Address }) => {
      const route =
        customerType === 'primary'
          ? address.address_type === 'registration'
            ? `deals/${dealId}/primary-customer/${customerIdOrCoregistrantId}/registration-address`
            : `deals/${dealId}/primary-customer/${customerIdOrCoregistrantId}/delivery-address`
          : address.address_type === 'registration'
            ? `deals/${dealId}/co-registrant/${customerIdOrCoregistrantId}/registration-address`
            : `deals/${dealId}/co-registrant/${customerIdOrCoregistrantId}/delivery-address`;

      const response = await pdkAxios.post(`/${route}`, {
        address: {
          id: address.id,
          address_line_1: address.address_line_1,
          address_line_2: address.address_line_2,
          city: address.city,
          state: address.state,
          postal_code: address.postal_code,
        },
      });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      toast({
        title: 'Error updating address',
        description: error.message,
      });
      console.error('Error updating address', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealId] });
    },
  });

  const handleUpdatePrimaryCustomerRegistrationAddress = useCallback(
    debounce(
      (
        address1: string,
        address2: string,
        city: string,
        state: string,
        zip: string,
      ) => {
        if (
          address1 === address.address_line_1 &&
          address2 === address.address_line_2 &&
          city === address.city &&
          state === address.state &&
          zip === address.postal_code
        ) {
          return;
        }

        updateCustomerRegistrationAddressMutation.mutate({
          address: {
            id: address.id,
            customer_id: customerIdOrCoregistrantId,
            address_type: address.address_type,
            address_line_1: address1,
            address_line_2: address2,
            city: city,
            state: state,
            postal_code: zip,
          },
        });
      },
      1000,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const debouncedFetchAddresses = useCallback(
    debounce(async (query: string) => {
      if (!query) return;
      try {
        const addresses = await fetchAutocompleteAddresses(query);
        setOptions(addresses);
      } catch (error) {
        console.error('Error fetching addresses:', error);
        setOptions([]);
      }
    }, 300),
    [],
  );

  useEffect(() => {
    debouncedFetchAddresses(address1);
  }, [address1, debouncedFetchAddresses]);

  useEffect(() => {
    if (address1Ref.current && focusOnMount && !hasFocusedOnMount) {
      address1Ref.current.focus();
      setHasFocusedOnMount(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address1Ref.current, focusOnMount, hasFocusedOnMount]);

  const address1ContainerRef = useRef<HTMLDivElement>(null);
  useClickOutside(address1ContainerRef, () => setAddress1IsFocused(false));

  const address1HasChanged = address1 !== address.address_line_1;

  const showSuggestions =
    !isEmpty(address1) &&
    options.length > 0 &&
    address1IsFocused &&
    address1HasChanged;

  useEffect(() => {
    if (
      address1 === address.address_line_1 &&
      address2 === address.address_line_2 &&
      city === address.city &&
      state === address.state &&
      zip === address.postal_code
    ) {
      return;
    }

    setAddress1(address.address_line_1);
    setAddress2(address.address_line_2);
    setCity(address.city);
    setState(address.state);
    setZip(address.postal_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address.address_line_1,
    address.address_line_2,
    address.city,
    address.state,
    address.postal_code,
  ]);

  return (
    <div className="flex flex-col gap-1 w-full dark:bg-dark dark:text-white">
      <div
        className="address1-container relative flex flex-row gap-2 items-center dark:bg-dark dark:text-white"
        ref={address1ContainerRef}
      >
        <Command className="absolute z-50 top-0 left-0 rounded-none border-none shadow-none h-fit p-0 w-full bg-transparent dark:bg-dark dark:text-white">
          <div className="flex flex-row gap-2 items-center">
            <CommandInput
              placeholder="Address1"
              onValueChange={(value: string) => setAddress1(value || '')}
              value={address1}
              hideAdornment
              wrapperClassName="border-none shadow-none h-fit p-0 bg-transparent w-full"
              className="h-fit p-0 w-full text-gray-500 py-0 placeholder:text-gray-400 text-sm bg-transparent dark:bg-dark dark:text-white"
              ref={address1Ref}
              onFocus={() => setAddress1IsFocused(true)}
              onBlur={(e) => {
                const newAddress1 = e.target.value;
                handleUpdatePrimaryCustomerRegistrationAddress(
                  newAddress1,
                  address2,
                  city,
                  state,
                  zip,
                );
              }}
            />
          </div>

          <CommandList className="border-none shadow-none h-fit w-full bg-white dark:bg-dark dark:text-white">
            {showSuggestions &&
              options.map((option) => (
                <CommandItem
                  className="bg-white dark:bg-dark dark:text-white dark:hover:bg-white/40 dark:active:bg-white/40"
                  key={option?.address}
                  value={option?.address}
                  onSelect={() => {
                    setAddress1(option?.short_address);
                    setAddress2('');
                    setCity(option?.city);
                    setState(option?.state);
                    setZip(option?.zip);
                    setAddress1IsFocused(false);

                    handleUpdatePrimaryCustomerRegistrationAddress(
                      option?.short_address,
                      '',
                      option?.city,
                      option?.state,
                      option?.zip,
                    );
                  }}
                >
                  {option?.address}
                </CommandItem>
              ))}
          </CommandList>
        </Command>
      </div>
      <div className="w-1/2 h-5">
        {/* Intentional empty div to mimic the height and width of address1-container input */}
      </div>

      <InlineTextInput
        ref={address2Ref}
        placeholder="Address2"
        onBlur={(e) => {
          // if (isEmpty(e?.target?.value)) {
          //   setShowAddress2(false);
          // }
          handleUpdatePrimaryCustomerRegistrationAddress(
            address1,
            e?.target?.value,
            city,
            state,
            zip,
          );
        }}
        value={address2}
        onChange={(e) => setAddress2(e?.target?.value)}
        wrapperClassName="w-full!"
        autoFocus={false}
      />

      <div className="flex flex-row gap-2 items-center justify-between">
        <InlineTextInput
          placeholder="City"
          value={city}
          onChange={(e) => {
            setCity(e?.target?.value);
            handleUpdatePrimaryCustomerRegistrationAddress(
              address1,
              address2,
              e?.target?.value,
              state,
              zip,
            );
          }}
          wrapperClassName="w-full!"
        />
        <InlineTextInput
          placeholder="State"
          value={state}
          onChange={(e) => {
            setState(e?.target?.value);
            handleUpdatePrimaryCustomerRegistrationAddress(
              address1,
              address2,
              city,
              e?.target?.value,
              zip,
            );
          }}
          wrapperClassName="w-full!"
        />

        <InlineTextInput
          placeholder="Zip"
          value={zip}
          onChange={(e) => {
            setZip(e?.target?.value);
            handleUpdatePrimaryCustomerRegistrationAddress(
              address1,
              address2,
              city,
              state,
              e?.target?.value,
            );
          }}
          wrapperClassName="w-full!"
        />
      </div>
    </div>
  );
};

export default AddressForm;
